import {
  ArrowRight,
  CalendarDays,
  Plus,
  Swords,
  TrendingUp,
} from "lucide-react";
import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { useMemo, type CSSProperties } from "react";
import { Link } from "react-router";

import { CompetitorAvatar } from "@/components/competitors/CompetitorAvatar";
import { activityKindLabelKey } from "@/components/competitors/intel";
import { PlatformBadge } from "@/components/competitors/PlatformIcon";
import { competitorPath, trendsPath } from "@/components/competitors/routes";
import type { CompetitorMarketingActivity } from "@/components/competitors/types";
import { SectionCard, SoftChip } from "@/components/competitors/ui";
import { formatShortDate, toIsoDay } from "@/components/market-intel/dates";
import { LoadingCards } from "@/components/market-intel/components";
import type { MarketCompetitor } from "@/components/market-intel/types";
import { Button } from "@/components/ui/button";
import { useMoneyFormat } from "@/hooks/use-money-format";

import { weeklyKeySeries } from "./analytics";
import { useMarketing } from "./context";
import { findById } from "./hooks";
import { PieceRow } from "./PieceRow";
import { marketingTabPath } from "./routes";
import {
  marketingKpis,
  recentCompetitorActivity,
  upcomingPieces,
} from "./summary";
import { KpiTile } from "./ui";

export const SummaryView = ({ isPending }: { isPending: boolean }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { formatMoney } = useMoneyFormat();
  const { data, newPiece } = useMarketing();
  const today = toIsoDay(new Date());
  const kpis = useMemo(
    () => marketingKpis(data.items, data.metrics, today),
    [data.items, data.metrics, today],
  );
  const upcoming = useMemo(
    () => upcomingPieces(data.items, new Date()),
    [data.items],
  );
  const lastWeek = useMemo(() => {
    const series = weeklyKeySeries(data.metrics);
    return [...series]
      .reverse()
      .find((point) => point.saves + point.shares + point.direct_messages > 0);
  }, [data.metrics]);

  if (isPending) return <LoadingCards />;

  return (
    <section className="space-y-4 md:space-y-5">
      <h2 className="sr-only">{translate("marketing.summary.heading")}</h2>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        <KpiTile
          index={0}
          label={translate("marketing.summary.kpi_scheduled")}
          value={kpis.scheduledThisWeek}
        />
        <KpiTile
          index={1}
          label={translate("marketing.summary.kpi_published")}
          value={kpis.publishedThisMonth}
        />
        <KpiTile
          index={2}
          label={translate("marketing.summary.kpi_leads")}
          value={kpis.leadsThisMonth}
        />
        <KpiTile
          index={3}
          label={translate("marketing.summary.kpi_spend")}
          value={formatMoney(kpis.spendThisMonth)}
        />
      </div>
      {lastWeek ? (
        <div
          className="mk-keyweek mi-rise"
          style={{ "--mi-index": 4 } as CSSProperties}
        >
          <div className="min-w-0">
            <p className="text-xs font-semibold tracking-wide uppercase opacity-75">
              {translate("marketing.analytics.week_of", {
                day: formatShortDate(lastWeek.week, locale),
              })}
            </p>
            <p className="mt-0.5 text-xs opacity-75">
              {translate("marketing.summary.key_week_hint")}
            </p>
          </div>
          <dl className="mk-keyweek__numbers">
            {(["saves", "shares", "direct_messages"] as const).map((key) => (
              <div key={key}>
                <dt>{translate(`marketing.metric.${key}`)}</dt>
                <dd>{lastWeek[key]}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <SectionCard
          id="mk-upcoming"
          icon={CalendarDays}
          title={translate("marketing.summary.upcoming")}
          count={upcoming.length || undefined}
          action={
            <Button
              type="button"
              variant="ghost"
              onClick={() => newPiece()}
              aria-label={translate("marketing.common.new_piece")}
              className="h-9 shrink-0 rounded-full bg-primary/8 px-2.5 text-sm font-semibold text-primary hover:bg-primary/12 sm:px-3.5 dark:bg-primary/15"
            >
              <Plus aria-hidden className="size-4" />
              <span className="max-sm:sr-only">
                {translate("marketing.common.new_piece")}
              </span>
            </Button>
          }
        >
          {upcoming.length ? (
            <ul className="-mx-1 flex flex-col">
              {upcoming.map((piece, index) => (
                <PieceRow key={piece.id} piece={piece} index={index} />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              {translate("marketing.summary.upcoming_empty")}
            </p>
          )}
          <Link to={marketingTabPath("calendar")} className="mk-link mt-3">
            {translate("marketing.summary.see_calendar")}
            <ArrowRight aria-hidden className="size-4" />
          </Link>
        </SectionCard>
        <CompetitorActivityCard />
      </div>
    </section>
  );
};

/** Latest competitor_marketing_activity, each linked to its competitor. */
export const CompetitorActivityCard = () => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { data: activity = [] } = useGetList<CompetitorMarketingActivity>(
    "competitor_marketing_activity",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "published_at", order: "DESC" },
    },
  );
  const { data: competitors = [] } = useGetList<MarketCompetitor>(
    "market_competitors",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "name", order: "ASC" },
    },
  );
  const recent = recentCompetitorActivity(activity);

  return (
    <SectionCard
      id="mk-competition"
      icon={Swords}
      title={translate("marketing.summary.competitors")}
    >
      {recent.length ? (
        <ul className="flex flex-col">
          {recent.map((entry) => {
            const competitor = findById(competitors, entry.competitor_id);
            return (
              <li key={entry.id}>
                <Link
                  to={competitorPath(entry.competitor_id)}
                  className="mk-row group"
                >
                  {competitor ? (
                    <CompetitorAvatar competitor={competitor} />
                  ) : null}
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                      <PlatformBadge platform={entry.platform} size="sm" />
                      <span className="truncate font-semibold text-foreground">
                        {competitor?.name}
                      </span>
                      {entry.published_at ? (
                        <span className="shrink-0">
                          · {formatShortDate(entry.published_at, locale)}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-sm leading-snug font-medium [overflow-wrap:anywhere] group-hover:text-primary">
                      {entry.title}
                    </span>
                    <span>
                      <SoftChip>
                        {translate(activityKindLabelKey(entry.kind))}
                      </SoftChip>
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("marketing.summary.competitors_empty")}
        </p>
      )}
      <Link to={trendsPath} className="mk-link mt-3">
        <TrendingUp aria-hidden className="size-4" />
        {translate("marketing.summary.see_trends")}
        <ArrowRight aria-hidden className="size-4" />
      </Link>
    </SectionCard>
  );
};
